// Pure decisions behind useSheetDrag. No DOM, no refs: each takes numbers and
// answers one question, so the gesture hook stays a thin wiring layer.

/** Release farther than this fraction of the card height dismisses. */
const DISMISS_FRACTION = 0.3
/** Release faster than this (px/ms, downward) dismisses. */
const DISMISS_VELOCITY = 0.5
/** Velocity is measured over the last ~80ms of moves. */
const VELOCITY_WINDOW_MS = 80
/** Upward drag is rubber-banded: dy * 0.2, never more than this. */
const RUBBER = 0.2
const RUBBER_CAP = 24
/** Movement before a body gesture is classified (px). */
const SLOP = 6

export interface Move { t: number; y: number }
export type MoveClass = 'wait' | 'ignore' | 'drag'

/** Drop moves older than the velocity window, always keeping the last two. */
export function trimMoves(moves: Move[], now: number): void {
  while (moves.length > 2 && now - moves[0].t > VELOCITY_WINDOW_MS) moves.shift()
}

/** Downward px/ms over the recorded moves; 0 when no time has passed. */
export function velocity(moves: Move[]): number {
  const first = moves[0]
  const last = moves[moves.length - 1]
  const dt = last.t - first.t
  return dt > 0 ? (last.y - first.y) / dt : 0
}

/** Does a release at offset `dy` with velocity `v` dismiss a card of `height`? */
export function shouldDismiss(dy: number, height: number, v: number): boolean {
  return dy > height * DISMISS_FRACTION || (dy > 0 && v > DISMISS_VELOCITY)
}

/** Downward drag follows the finger; upward is damped and capped. */
export function rubberBand(rawDy: number): number {
  return rawDy >= 0 ? rawDy : -Math.min(RUBBER_CAP, -rawDy * RUBBER)
}

/**
 * Classify a gesture's first real move. Header: any move past the slop drags.
 * Body: must be downward, mostly vertical, and the body still at the top.
 */
export function classifyMove(from: 'header' | 'body', dx: number, dy: number, bodyScrollTop: number): MoveClass {
  if (Math.abs(dy) < SLOP && Math.abs(dx) < SLOP) return 'wait'
  if (from === 'body' && (dy <= 0 || Math.abs(dx) > Math.abs(dy) || bodyScrollTop > 0)) return 'ignore'
  return 'drag'
}

/** A body gesture at the top moving mostly downward: refuse the native pan now. */
export function isPullDownAtTop(from: 'header' | 'body', bodyScrollTop: number, dx: number, dy: number): boolean {
  return from === 'body' && bodyScrollTop <= 0 && dy > 0 && dy > Math.abs(dx)
}
